<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed } from 'vue';

  import type { Call } from '@stuut/api/models';
  import { humanize } from '@stuut/common/src/utils';
  import { Separator } from '@ui/components/separator';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import TaskReplyWarning from '@/components/tasks/TaskReplyWarning.vue';
  import { HIGH_ALERT_DAY_THRESHOLD } from '@/constants/constants';

  interface Props {
    alertDayThreshold?: number;
    call?: Call;
    highAlertDayThreshold?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    highAlertDayThreshold: HIGH_ALERT_DAY_THRESHOLD,
  });

  const daysElapsed = computed(() => props.call?.oldestOpenResponseTask?.daysElapsed ?? 0);

  const indicatorStateClass = computed(() => {
    if (props.call?.openTasks?.length) {
      if (daysElapsed.value >= props.highAlertDayThreshold) {
        return 'text-foreground border';
      }

      return 'text-foreground border';
    }

    if (!props.call?.openTasks?.length) {
      return 'bg-secondary border text-muted-foreground';
    }

    return '';
  });
</script>

<template>
  <Tooltip v-if="call?.tasks?.length" :delay-duration="0">
    <TooltipTrigger v-bind="$attrs">
      <div
        class="rounded-full inline-flex items-center justify-center size-6 relative"
        :class="indicatorStateClass"
      >
        <template v-if="call?.openTasks?.length">
          <i class="fas fa-tasks"></i>
          <div
            class="size-2 rounded-full absolute z-10 top-0 -right-0.5 text-[3px]"
            :class="daysElapsed >= highAlertDayThreshold && 'bg-error'"
          ></div>
        </template>

        <i v-else class="far fa-check text-xs! text-success"></i>
      </div>
    </TooltipTrigger>

    <TooltipContent side="left">
      <div class="space-y-1">
        <p class="first-letter:uppercase">
          <template v-if="call?.openTasks?.length">
            {{ humanize(call?.object) }} contains
            {{ pluralize('open task', call?.openTasks?.length, true) }}
          </template>

          <template v-else> All tasks closed or completed. </template>
        </p>

        <template v-if="call?.openResponseTasks?.length">
          <Separator />

          <div class="flex items-center gap-x-1.5 text-xs">
            <i class="fa fas fa-reply text-agent text-xs!" />

            <strong>
              {{ pluralize('task', call?.openResponseTasks?.length, true) }}
              requiring a response
            </strong>
          </div>

          <TaskReplyWarning :task="call?.oldestOpenResponseTask" />
        </template>

        <template v-else>
          <template v-if="call?.completedTasks?.length || call?.closedTasks?.length">
            <Separator />
          </template>

          <div v-if="call?.completedTasks?.length" class="flex items-center gap-x-1.5 text-xs">
            <i class="fa far fa-check-circle text-success text-xs!" />
            {{ call?.completedTasks?.length }} completed
          </div>

          <div v-if="call?.closedTasks?.length" class="flex items-center gap-x-1.5 text-xs">
            <i class="fa far fa-circle-x text-muted-foreground text-xs!" />
            {{ call?.closedTasks?.length }} dismissed
          </div>
        </template>
      </div>
    </TooltipContent>
  </Tooltip>
</template>
